/**
 * A-1 商品（admin.products 權限；CMS 商品 CRUD）+ pricing 表單用的商品 lookup。
 */

import { apiFetch, type Schemas } from './core';

/**
 * 與 BE apps/api/src/modules/products/dto.ts 對齊。
 * - PRODUCT_CATEGORIES = ['trip','experience','package','charter']
 * - PRODUCT_CHANNELS = ['web','kiosk','partner','floor']
 * - SUPPORTED_CURRENCIES = ['TWD','USD','JPY']
 */
export const PRODUCT_CATEGORIES = ['trip', 'experience', 'package', 'charter'] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const PRODUCT_CHANNELS = ['web', 'kiosk', 'partner'] as const;
export type ProductChannel = (typeof PRODUCT_CHANNELS)[number];

export const PRODUCT_CURRENCIES = ['TWD', 'USD', 'JPY'] as const;
export type ProductCurrency = (typeof PRODUCT_CURRENCIES)[number];

export type ProductSummary = Schemas['ProductResponseDto'];

export interface CreateProductDto {
  sku: string;
  category: ProductCategory;
  name_i18n: Record<string, string>;
  description_i18n?: Record<string, string>;
  base_price: number;
  /** 非成人票種原價表 {ticket_type: price}（成人原價用 base_price）；空 = 只賣成人。 */
  /** 送出時 number 與數值字串皆可；後端一律正規化為字串。 */
  ticket_prices?: Record<string, number | string>;
  /**
   * 住客成人原價；`null` / 省略 = 未設住客價（與非住客同價）。
   *
   * 住客價**整組**取代非住客價，不逐欄位混用：設了它，住客的可售票種也改由
   * `guest_ticket_prices` 決定。
   */
  guest_base_price?: number | null;
  guest_ticket_prices?: Record<string, number | string>;
  currency?: ProductCurrency;
  duration_minutes?: number;
  channels?: ProductChannel[];
}

export interface UpdateProductDto extends Partial<Omit<CreateProductDto, 'sku'>> {
  is_published?: boolean;
}

export interface ListProductsResult {
  items: ProductSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListProductsOptions {
  limit?: number;
  offset?: number;
}

export function listProducts(opts: ListProductsOptions = {}): Promise<ListProductsResult> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return apiFetch<ListProductsResult>(`/products${qs ? `?${qs}` : ''}`, { method: 'GET' });
}

export function getProduct(id: string): Promise<ProductSummary> {
  return apiFetch<ProductSummary>(`/products/${encodeURIComponent(id)}`, { method: 'GET' });
}

export function createProduct(dto: CreateProductDto): Promise<ProductSummary> {
  return apiFetch<ProductSummary>('/products', { method: 'POST', body: dto });
}

export function updateProduct(id: string, dto: UpdateProductDto): Promise<ProductSummary> {
  /**
   * BE 之 PATCH 不收 is_published（is_published 改走 /products/:id/publish |
   * /products/:id/unpublish）。CMS 端為 UX 簡潔，於此 wrapper 內拆呼叫：
   *   1. 若 dto.is_published 有給且需切換 → 呼 publish / unpublish endpoint
   *   2. 其他欄位走 PATCH
   * 兩段失敗任一即拋出 ApiError；publish 切換先做以便讓 PATCH 之 422 不會吞掉
   * 排程旗標被清的副作用。
   */
  const { is_published, ...rest } = dto;
  const hasPatchFields = Object.keys(rest).length > 0;

  const togglePublish = async (): Promise<ProductSummary | undefined> => {
    if (is_published === undefined) return undefined;
    const path = is_published
      ? `/products/${encodeURIComponent(id)}/publish`
      : `/products/${encodeURIComponent(id)}/unpublish`;
    return apiFetch<ProductSummary>(path, { method: 'POST' });
  };

  return (async (): Promise<ProductSummary> => {
    const toggled = await togglePublish();
    if (!hasPatchFields) {
      return toggled ?? (await getProduct(id));
    }
    return apiFetch<ProductSummary>(`/products/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: rest,
    });
  })();
}

// ---------- 商品 lookup（pricing select 用）----------

export interface ProductLite {
  id: string;
  sku: string;
  name_i18n: Record<string, string>;
  is_published: boolean;
  /** 商品定價（numeric string）；pricing 表單選商品後自動帶入牌價基準。 */
  base_price: string;
  /** 非成人票種原價表；pricing 表單據此限定票種下拉 + 帶入 per-type 原價。 */
  /** 值為 2 位小數字串（同 base_price 格式）。 */
  ticket_prices: Record<string, string> | null;
}

export interface ListProductsLiteResult {
  items: ProductLite[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * 取商品 lookup（給 pricing 表單之 product select 用）。
 * 後端 GET /products 預設過濾已軟刪除；以 limit=200 取一頁就夠 CMS 表單顯示。
 */
export function listProductsLite(): Promise<ListProductsLiteResult> {
  return apiFetch<ListProductsLiteResult>('/products?limit=200', { method: 'GET' });
}
